import { useLayoutEffect, type ReactNode } from 'react';

import { useThemeStore, type Theme } from '@/modules/core';

interface ThemeProviderProps {
  children: ReactNode;
  /** Applied instead of the stored theme, without changing the user's saved choice. */
  forcedTheme?: Theme;
}

/**
 * ThemeProvider applies the selected theme to the document by toggling the
 * `dark` class on the <html> element. Tailwind is configured with
 * `darkMode: ['class']`, so this class switches every token-based color.
 *
 * The theme is read from useThemeStore (default dark, persisted to localStorage).
 * index.html sets the class before first paint to avoid a flash; this keeps it
 * in sync with later toggles.
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children, forcedTheme }: ThemeProviderProps) {
  const { theme: storedTheme } = useThemeStore();
  const theme = forcedTheme ?? storedTheme;

  // Layout effect, so a forced theme that differs from index.html's guess is
  // swapped in before the first paint.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return children;
}
